import React, { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { base44 } from "api/apiClient";
import { useRestaurant } from "../contexts/RestaurantContext";
import { Button } from "components/ui/button";
import { Badge } from "components/ui/badge";
import {
  Bot,
  Send,
  Loader2,
  Sparkles,
  X,
  Mic,
  MicOff,
  Plus,
  Maximize2,
  ChevronDown,
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeSanitize from "rehype-sanitize";
import { format } from "date-fns";

// ---------------------------------------------------------------------------
// Markdown components shared by message rendering
// ---------------------------------------------------------------------------
const mdComponents = {
  p: ({ children }) => <p className="my-1.5 leading-relaxed">{children}</p>,
  ul: ({ children }) => (
    <ul className="my-1.5 ml-4 list-disc space-y-0.5">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="my-1.5 ml-4 list-decimal space-y-0.5">{children}</ol>
  ),
  li: ({ children }) => <li className="my-0.5">{children}</li>,
  h1: ({ children }) => (
    <h1 className="text-base font-bold my-2">{children}</h1>
  ),
  h2: ({ children }) => (
    <h2 className="text-sm font-bold my-1.5">{children}</h2>
  ),
  h3: ({ children }) => (
    <h3 className="text-sm font-semibold my-1">{children}</h3>
  ),
  strong: ({ children }) => (
    <strong className="font-semibold text-slate-900">{children}</strong>
  ),
  code: ({ inline, children }) =>
    inline ? (
      <code className="px-1 py-0.5 rounded bg-slate-100 text-slate-700 text-xs font-mono">
        {children}
      </code>
    ) : (
      <code className="block bg-slate-900 text-slate-100 rounded-lg p-2.5 my-1.5 text-xs font-mono overflow-x-auto">
        {children}
      </code>
    ),
  table: ({ children }) => (
    <div className="my-2 overflow-x-auto rounded-lg border border-slate-200">
      <table className="min-w-full text-xs">{children}</table>
    </div>
  ),
  thead: ({ children }) => (
    <thead className="bg-slate-50 border-b border-slate-200">{children}</thead>
  ),
  th: ({ children }) => (
    <th className="px-2.5 py-1.5 text-left font-semibold text-slate-700">
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td className="px-2.5 py-1.5 border-t border-slate-100">{children}</td>
  ),
  tr: ({ children }) => <tr className="even:bg-slate-50/50">{children}</tr>,
};

// ---------------------------------------------------------------------------
// Message bubble
// ---------------------------------------------------------------------------
function MessageBubble({ message, t }) {
  const isUser = message.role === "user";

  return (
    <div className={`flex gap-2.5 ${isUser ? "justify-end" : "justify-start"}`}>
      {!isUser && (
        <div className="h-7 w-7 rounded-lg bg-emerald-700 flex items-center justify-center mt-0.5 flex-shrink-0">
          <Bot className="w-3.5 h-3.5 text-white" />
        </div>
      )}
      <div className={`max-w-[85%] ${isUser ? "flex flex-col items-end" : ""}`}>
        {message.content && (
          <div
            className={`rounded-2xl px-3.5 py-2.5 ${
              isUser
                ? "bg-emerald-700 text-white"
                : "bg-white border border-slate-200 shadow-sm"
            }`}
          >
            {isUser ? (
              <p className="text-sm leading-relaxed">{message.content}</p>
            ) : (
              <ReactMarkdown
                className="text-sm prose prose-sm prose-slate max-w-none [&>*:first-child]:mt-0 [&>*:last-child]:mb-0"
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeSanitize]}
                components={mdComponents}
              >
                {message.content}
              </ReactMarkdown>
            )}
          </div>
        )}

        {message.tool_calls && message.tool_calls.length > 0 && (
          <div className="mt-1.5 space-y-1">
            {message.tool_calls.map((toolCall, idx) => (
              <div
                key={idx}
                className="text-xs bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5"
              >
                <div className="flex items-center gap-1.5">
                  <Sparkles className="w-3 h-3 text-emerald-600" />
                  <span className="font-medium text-slate-600">
                    {toolCall.name?.split(".").pop() || t("carlo.thinking")}
                  </span>
                  {toolCall.status === "completed" && (
                    <Badge className="ml-auto bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px] px-1.5 py-0">
                      OK
                    </Badge>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Quick action pills
// ---------------------------------------------------------------------------
function QuickActions({ onSelect, t }) {
  const actions = [
    { label: t("carlo.quickLowStock"), prompt: "What's running low?" },
    { label: t("carlo.quickForecast"), prompt: "What's the forecast for today?" },
    { label: t("carlo.quickTasks"), prompt: "Show me pending tasks" },
    { label: t("carlo.quickSales"), prompt: "How were today's sales?" },
  ];

  return (
    <div className="flex flex-wrap gap-1.5 px-1">
      {actions.map((a, i) => (
        <button
          key={i}
          onClick={() => onSelect(a.prompt)}
          className="text-xs px-3 py-1.5 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 transition-colors"
        >
          {a.label}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main CarloChat component
// ---------------------------------------------------------------------------
export default function CarloChat() {
  const { t } = useTranslation();
  const { currentRestaurant } = useRestaurant();

  // Panel state
  const [isOpen, setIsOpen] = useState(false);
  const [hasNewResponse, setHasNewResponse] = useState(false);

  // Conversations
  const [conversations, setConversations] = useState([]);
  const [currentConversationId, setCurrentConversationId] = useState(null);
  const [showConvoDropdown, setShowConvoDropdown] = useState(false);

  // Messages
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Voice
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  // Refs
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  // -----------------------------------------------------------------------
  // Scroll to bottom
  // -----------------------------------------------------------------------
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Focus input when panel opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // -----------------------------------------------------------------------
  // Load conversations on mount and when restaurant changes
  // -----------------------------------------------------------------------
  const loadConversations = useCallback(async () => {
    try {
      const convos = await base44.agents.listConversations({
        agent_name: "carlo_forecast",
      });
      setConversations(convos || []);
      return convos || [];
    } catch {
      return [];
    }
  }, []);

  useEffect(() => {
    loadConversations().then((convos) => {
      if (convos.length > 0 && !currentConversationId) {
        setCurrentConversationId(convos[0].id);
      }
    });
  }, [loadConversations]); // eslint-disable-line react-hooks/exhaustive-deps

  // -----------------------------------------------------------------------
  // Load messages when switching conversation
  // -----------------------------------------------------------------------
  useEffect(() => {
    if (!currentConversationId) {
      setMessages([]);
      return;
    }
    base44.agents
      .getConversation(currentConversationId)
      .then((data) => setMessages(data.messages || []))
      .catch(() => setMessages([]));
  }, [currentConversationId]);

  // -----------------------------------------------------------------------
  // Close dropdown on outside click
  // -----------------------------------------------------------------------
  useEffect(() => {
    function handleClick(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowConvoDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // -----------------------------------------------------------------------
  // Send message
  // -----------------------------------------------------------------------
  const sendMessage = useCallback(
    async (text) => {
      const trimmed = (text || input).trim();
      if (!trimmed || isLoading) return;

      setIsLoading(true);
      setInput("");
      setMessages((prev) => [...prev, { role: "user", content: trimmed }]);

      try {
        let convoId = currentConversationId;

        // Auto-create conversation if none
        if (!convoId) {
          const conversation = await base44.agents.createConversation({
            agent_name: "carlo_forecast",
            metadata: {
              name: `Carlo ${format(new Date(), "dd.MM.yyyy HH:mm")}`,
            },
          });
          convoId = conversation.id;
          setCurrentConversationId(convoId);
          loadConversations();
        }

        const result = await base44.agents.addMessage({ id: convoId }, { content: trimmed });

        if (result.assistant_message) {
          setMessages((prev) => [...prev, result.assistant_message]);
          if (!isOpen) setHasNewResponse(true);
        }
      } catch {
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: "Sorry, there was an error processing your message. Please try again.",
          },
        ]);
      } finally {
        setIsLoading(false);
      }
    },
    [input, isLoading, currentConversationId, isOpen, loadConversations]
  );

  // -----------------------------------------------------------------------
  // Keyboard handler
  // -----------------------------------------------------------------------
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // -----------------------------------------------------------------------
  // New conversation
  // -----------------------------------------------------------------------
  const createNewConversation = async () => {
    try {
      const conversation = await base44.agents.createConversation({
        agent_name: "carlo_forecast",
        metadata: {
          name: `Carlo ${format(new Date(), "dd.MM.yyyy HH:mm")}`,
        },
      });
      setCurrentConversationId(conversation.id);
      setMessages([]);
      setShowConvoDropdown(false);
      loadConversations();
    } catch (err) {
      console.error("Error creating conversation:", err);
    }
  };

  // -----------------------------------------------------------------------
  // Voice recording
  // -----------------------------------------------------------------------
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported("audio/webm")
          ? "audio/webm"
          : "audio/mp4",
      });

      audioChunksRef.current = [];
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach((track) => track.stop());
        const mimeType = mediaRecorder.mimeType;
        const blob = new Blob(audioChunksRef.current, { type: mimeType });

        if (blob.size < 100) return; // Too short, skip

        setIsTranscribing(true);
        try {
          const result = await base44.agents.transcribe(blob, "de");
          if (result?.text) {
            setInput((prev) => (prev ? prev + " " + result.text : result.text));
            inputRef.current?.focus();
          }
        } catch (err) {
          console.error("Transcription error:", err);
        } finally {
          setIsTranscribing(false);
        }
      };

      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start();
      setIsRecording(true);
    } catch (err) {
      console.error("Microphone access error:", err);
      alert(t("carlo.voiceError"));
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
  };

  const toggleRecording = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  // -----------------------------------------------------------------------
  // Toggle panel
  // -----------------------------------------------------------------------
  const togglePanel = () => {
    setIsOpen(!isOpen);
    if (!isOpen) setHasNewResponse(false);
  };

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------
  const restaurantName = currentRestaurant?.name || "Carlo";

  return (
    <>
      {/* Floating bubble button */}
      {!isOpen && (
        <button
          onClick={togglePanel}
          className={`fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-emerald-700 hover:bg-emerald-800 text-white shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center ${
            hasNewResponse ? "animate-pulse" : ""
          }`}
          title="Carlo"
        >
          <Bot className="w-6 h-6" />
        </button>
      )}

      {/* Chat panel */}
      {isOpen && (
        <div className="fixed bottom-0 right-0 z-50 md:bottom-6 md:right-6 w-full h-full md:w-[400px] md:h-[550px] md:rounded-2xl bg-white md:shadow-2xl flex flex-col overflow-hidden md:border md:border-slate-200">
          {/* Header */}
          <div className="bg-emerald-700 text-white px-4 py-3 flex items-center gap-3 flex-shrink-0">
            <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
              <Bot className="w-4 h-4 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-sm leading-tight">Carlo</h3>
              <p className="text-xs text-white/70 truncate">{restaurantName}</p>
            </div>
            <div className="flex items-center gap-1">
              <a
                href="/carlo-agent"
                className="p-1.5 rounded-lg hover:bg-white/15 transition-colors"
                title={t("carlo.expand")}
              >
                <Maximize2 className="w-4 h-4" />
              </a>
              <button
                onClick={togglePanel}
                className="p-1.5 rounded-lg hover:bg-white/15 transition-colors"
                title={t("carlo.close")}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Conversation selector */}
          <div className="px-3 py-2 border-b border-slate-100 flex items-center gap-2 flex-shrink-0 bg-slate-50/50">
            <div className="relative flex-1" ref={dropdownRef}>
              <button
                onClick={() => setShowConvoDropdown(!showConvoDropdown)}
                className="w-full text-left text-xs px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 transition-colors flex items-center justify-between"
              >
                <span className="truncate text-slate-600">
                  {conversations.find((c) => c.id === currentConversationId)
                    ?.metadata?.name ||
                    t("carlo.newConversation")}
                </span>
                <ChevronDown className="w-3 h-3 text-slate-400 flex-shrink-0 ml-1" />
              </button>

              {showConvoDropdown && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-10 max-h-48 overflow-y-auto">
                  {conversations.slice(0, 10).map((convo) => (
                    <button
                      key={convo.id}
                      onClick={() => {
                        setCurrentConversationId(convo.id);
                        setShowConvoDropdown(false);
                      }}
                      className={`w-full text-left text-xs px-3 py-2 hover:bg-slate-50 transition-colors border-b border-slate-50 last:border-0 ${
                        convo.id === currentConversationId
                          ? "bg-emerald-50 text-emerald-800"
                          : "text-slate-600"
                      }`}
                    >
                      <span className="block truncate font-medium">
                        {convo.metadata?.name || "Carlo Session"}
                      </span>
                      <span className="text-[10px] text-slate-400">
                        {format(new Date(convo.created_date), "dd.MM.yyyy HH:mm")}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button
              onClick={createNewConversation}
              className="p-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 transition-colors"
              title={t("carlo.newConversation")}
            >
              <Plus className="w-3.5 h-3.5 text-slate-500" />
            </button>
          </div>

          {/* Messages area */}
          <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3">
            {messages.length === 0 && !isLoading ? (
              <div className="flex flex-col items-center justify-center h-full py-6">
                <div className="w-14 h-14 bg-emerald-100 rounded-2xl flex items-center justify-center mb-4">
                  <Bot className="w-7 h-7 text-emerald-700" />
                </div>
                <p className="text-sm font-medium text-slate-700 mb-1">
                  {t("carlo.title")}
                </p>
                <p className="text-xs text-slate-500 text-center mb-4 px-4">
                  {t("carlo.placeholder")}
                </p>
                <QuickActions
                  onSelect={(prompt) => sendMessage(prompt)}
                  t={t}
                />
              </div>
            ) : (
              messages.map((msg, idx) => (
                <MessageBubble key={idx} message={msg} t={t} />
              ))
            )}

            {isLoading && (
              <div className="flex gap-2.5 justify-start">
                <div className="h-7 w-7 rounded-lg bg-emerald-700 flex items-center justify-center flex-shrink-0">
                  <Bot className="w-3.5 h-3.5 text-white" />
                </div>
                <div className="bg-white border border-slate-200 rounded-2xl px-3.5 py-2.5 shadow-sm">
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-3.5 h-3.5 animate-spin text-emerald-600" />
                    <span className="text-xs text-slate-500">
                      {t("carlo.thinking")}
                    </span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input area */}
          <div className="border-t border-slate-200 bg-white px-3 py-2.5 flex-shrink-0">
            <div className="flex items-end gap-2">
              <div className="flex-1 relative">
                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={t("carlo.placeholder")}
                  disabled={isLoading}
                  rows={1}
                  className="w-full text-sm px-3 py-2 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-emerald-400 focus:ring-1 focus:ring-emerald-400 resize-none outline-none transition-colors placeholder:text-slate-400 disabled:opacity-50"
                  style={{
                    minHeight: "38px",
                    maxHeight: "96px",
                    height: "auto",
                    overflowY: input.split("\n").length > 3 ? "auto" : "hidden",
                  }}
                  onInput={(e) => {
                    e.target.style.height = "auto";
                    e.target.style.height =
                      Math.min(e.target.scrollHeight, 96) + "px";
                  }}
                />
              </div>

              {/* Mic button */}
              <button
                onClick={toggleRecording}
                disabled={isLoading || isTranscribing}
                className={`p-2 rounded-xl transition-colors flex-shrink-0 ${
                  isRecording
                    ? "bg-red-500 text-white hover:bg-red-600"
                    : isTranscribing
                    ? "bg-amber-100 text-amber-600"
                    : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                }`}
                title={
                  isRecording
                    ? t("carlo.recording")
                    : isTranscribing
                    ? t("carlo.transcribing")
                    : "Voice"
                }
              >
                {isTranscribing ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : isRecording ? (
                  <MicOff className="w-4 h-4" />
                ) : (
                  <Mic className="w-4 h-4" />
                )}
              </button>

              {/* Send button */}
              <Button
                onClick={() => sendMessage()}
                disabled={isLoading || !input.trim()}
                className="bg-emerald-700 hover:bg-emerald-800 rounded-xl px-3 h-[38px] flex-shrink-0"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
